/**
 * DrawerCtaSecondary - Secondary CTA button for calculator tooltip drawers
 *
 * Purpose: Hollow pink border button with transparent background
 * Matches: "Share Results" styling from calculator
 *
 * DO NOT use outside calculator drawers - this is drawer-specific styling
 * For site-wide CTAs, use ButtonCta component from /components/ui/ButtonCta.tsx
 */

'use client';

import React from 'react';
import { Loader2 } from '@/lib/icons';

interface DrawerCtaSecondaryProps {
  text: string;
  href?: string;
  onClick?: () => void;
  loading?: boolean;
  icon?: React.ReactNode;
  className?: string;
}

export const DrawerCtaSecondary: React.FC<DrawerCtaSecondaryProps> = ({
  text,
  href,
  onClick,
  loading = false,
  icon,
  className = '',
}) => {
  const baseClasses = "w-full flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-[#ff34a7] text-[#ff34a7] rounded-lg font-medium hover:bg-pink-50 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed";
  const finalClasses = `${baseClasses} ${className}`.trim();

  const content = (
    <>
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading...</span>
        </>
      ) : (
        <>
          {icon && <span className="w-4 h-4">{icon}</span>}
          <span>{text}</span>
        </>
      )}
    </>
  );

  // If href provided, render as link
  if (href && !loading) {
    return (
      <a href={href} className={finalClasses}>
        {content}
      </a>
    );
  }

  // Otherwise render as button
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={finalClasses}
    >
      {content}
    </button>
  );
};
